import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MuiMaterials } from "../@index.js";
import { createApiEndpoint } from "../index.js";
import * as global from "../data/global.js";
import {
    createWindowChromeTheme,
    getWindowChromeThemeColor,
    ModalBody,
    ModalContentTile,
    SiteSelectPhase,
    SiteWindowHeaderLeading,
    SiteWindowToolNav,
    ChromeHeaderBar,
    ChromeActionBar,
    Spinner,
    SITE_WINDOW_IDS,
    ensureSiteInfosLoaded,
} from "./windowChrome/index.js";

const getThemeColor = getWindowChromeThemeColor;

const HIDE_SCROLLBAR_SX = {
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": { display: "none" },
};

const sensorHealth = (s) => {
    if (!s.forcedOpen && s.flag === "No history") return "gray";
    if (s.forcedOpen || s.invalidDayPct >= 60 || s.flag === "Chronically invalid") return "red";
    if (
        s.invalidDayPct >= 20 ||
        s.flag === "Worsening hard" ||
        s.flag === "Worsening" ||
        s.flag === "Intermittent" ||
        (s.invalidPctDelta != null && s.invalidPctDelta >= 25)
    ) {
        return "yellow";
    }
    return "green";
};

const tipFoOnly = (sensors) => {
    const fo = sensors.filter((s) => s.forcedOpen);
    if (fo.length === 0) return true;
    const maxPos = Math.max(...sensors.map((s) => s.position));
    const foPos = fo.map((s) => s.position).sort((a, b) => a - b);
    const expected = Array.from({ length: fo.length }, (_, i) => maxPos - fo.length + 1 + i);
    return foPos.every((p, i) => p === expected[i]);
};

const classifyCable = (sensors) => {
    const n = sensors.length;
    const foCount = sensors.filter((s) => s.forcedOpen).length;
    const foRatio = n > 0 ? foCount / n : 0;
    const chronicN = sensors.filter(
        (s) => s.invalidDayPct >= 60 || s.flag === "Chronically invalid",
    ).length;
    if (
        foRatio >= 0.5 ||
        foCount >= Math.max(3, Math.ceil(n * 0.35)) ||
        chronicN >= Math.max(3, Math.ceil(n * 0.35))
    ) {
        return "red";
    }
    if (foCount >= 2 || (foCount >= 1 && !tipFoOnly(sensors)) || chronicN >= 2) return "yellow";
    return "green";
};

const binSortKey = (bin) => {
    const m = String(bin ?? "").match(/(\d+)/);
    return m ? Number(m[1]) : 0;
};

const cableSortKey = (name) => {
    const m = String(name ?? "").match(/-(\d+)$/);
    return m ? Number(m[1]) : 0;
};

const healthColor = (h) => {
    if (h === "red") return getThemeColor("#d32f2f", "#ef5350");
    if (h === "yellow") return getThemeColor("#c79100", "#c9a227");
    if (h === "gray") return getThemeColor("#9e9e9e", "#757575");
    return getThemeColor("#2e7d32", "#66bb6a");
};

const formatLastRead = (iso) => {
    if (iso == null || iso === "") return "—";
    const raw = String(iso).trim();
    const normalized = raw.includes("T") ? raw.replace("Z", "-05:00") : raw;
    const d = new Date(normalized);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-us", global.dateFormat).replaceAll("/", "-").replace(",", "");
};

const CableHealthWindow = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const autoLoadHandledRef = useRef(false);

    const [forceUpdate, setForceUpdate] = useState(0);
    const [phase, setPhase] = useState("select");
    const [sites, setSites] = useState([]);
    const [sitesLoading, setSitesLoading] = useState(true);
    const [pickerSiteGuid, setPickerSiteGuid] = useState(() =>
        location.state?.autoLoad && location.state?.siteGuid
            ? location.state.siteGuid
            : "",
    );
    const [loadedSiteGuid, setLoadedSiteGuid] = useState("");
    // Start loading when title-nav autoLoad carries a site so we never flash SiteSelectPhase.
    const [loading, setLoading] = useState(() =>
        Boolean(location.state?.autoLoad && location.state?.siteGuid),
    );
    const [error, setError] = useState("");
    const [payload, setPayload] = useState(null);

    const { theme, textPrimary, textSecondary, paper, border, pageBg } = useMemo(
        () => createWindowChromeTheme(),
        [forceUpdate],
    );

    useEffect(() => {
        const onTheme = () => setForceUpdate((n) => n + 1);
        window.addEventListener("themeChanged", onTheme);
        return () => window.removeEventListener("themeChanged", onTheme);
    }, []);

    useEffect(() => {
        let cancelled = false;
        const apply = () => {
            const all = [...(global.gw.siteInfos || [])].filter((s) => s.siteGuid && !s.isHms);
            all.sort((a, b) =>
                (a.siteName || "").toLowerCase().localeCompare((b.siteName || "").toLowerCase()),
            );
            if (cancelled) return;
            setSites(all);
            setSitesLoading(false);
        };
        if ((global.gw.siteInfos || []).length > 0) {
            apply();
            return undefined;
        }
        setSitesLoading(true);
        ensureSiteInfosLoaded().then(() => {
            if (!cancelled) apply();
        });
        return () => {
            cancelled = true;
        };
    }, [forceUpdate]);

    const loadHealth = useCallback(async (siteGuid) => {
        if (!siteGuid) return;
        setLoading(true);
        setError("");
        setPayload(null);
        try {
            const res = await createApiEndpoint().fetchSiteCableHealth("direct", siteGuid);
            if (!res?.ok) {
                setError(res?.message || "Failed to load cable health.");
                setPayload(null);
                setPhase("select");
                return;
            }
            setPayload(res);
            setLoadedSiteGuid(siteGuid);
            setPickerSiteGuid(siteGuid);
            setPhase("view");
        } catch (e) {
            setError(e?.message || "Failed to load cable health.");
            setPayload(null);
            setPhase("select");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const siteGuid = location.state?.siteGuid;
        if (!siteGuid || !location.state?.autoLoad) return;
        if (autoLoadHandledRef.current === siteGuid) return;
        autoLoadHandledRef.current = siteGuid;
        setPickerSiteGuid(siteGuid);
        loadHealth(siteGuid);
    }, [location.state, loadHealth]);

    const handleContinue = useCallback(() => {
        if (!pickerSiteGuid) return;
        loadHealth(pickerSiteGuid);
    }, [pickerSiteGuid, loadHealth]);

    const handleHeaderSiteChange = useCallback((siteGuid) => {
        if (!siteGuid) return;
        setPickerSiteGuid(siteGuid);
        loadHealth(siteGuid);
    }, [loadHealth]);

    const bins = useMemo(() => {
        const sensors = Array.isArray(payload?.sensors) ? payload.sensors : [];
        const byCable = new Map();
        for (const s of sensors) {
            const cable = s.cableName || "";
            const bin = s.binName || "";
            const key = `${bin}\0${cable}`;
            if (!byCable.has(key)) {
                byCable.set(key, { bin, cable, sensors: [] });
            }
            byCable.get(key).sensors.push({
                name: s.sensorName || "",
                position: Number(s.position) || 0,
                forcedOpen: !!s.forcedOpen,
                invalidDayPct: Number(s.invalidDayPct) || 0,
                validDayPct: Number(s.validDayPct) || 0,
                invalidPctDelta:
                    s.invalidPctDelta == null || s.invalidPctDelta === ""
                        ? null
                        : Number(s.invalidPctDelta),
                flag: s.flag || "",
                lastHistoryRead: s.lastHistoryRead || s.LastHistoryRead || null,
            });
        }
        const cables = [...byCable.values()].map((c) => ({
            ...c,
            health: classifyCable(c.sensors),
        }));
        const binNames = [...new Set(cables.map((c) => c.bin))].sort(
            (a, b) => binSortKey(a) - binSortKey(b),
        );
        return binNames.map((bin) => ({
            bin,
            cables: cables
                .filter((c) => c.bin === bin)
                .sort((a, b) => cableSortKey(a.cable) - cableSortKey(b.cable)),
        }));
    }, [payload]);

    const stats = useMemo(() => {
        const cables = bins.flatMap((b) => b.cables);
        const sensors = cables.flatMap((c) => c.sensors);
        const replaceCables = cables
            .filter((c) => c.health === "red")
            .map((c) => c.cable)
            .sort((a, b) => {
                const ba = binSortKey(a);
                const bb = binSortKey(b);
                if (ba !== bb) return ba - bb;
                return cableSortKey(a) - cableSortKey(b);
            });
        return {
            bins: bins.length,
            cables: cables.length,
            sensors: sensors.length,
            replace: replaceCables.length,
            replaceNames: replaceCables,
        };
    }, [bins]);

    return (
        <MuiMaterials.ThemeProvider theme={theme}>
            <div
                style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 1000,
                    display: "flex",
                    flexDirection: "column",
                    background: pageBg,
                    color: textPrimary,
                    paddingTop: "env(safe-area-inset-top, 0px)",
                    paddingBottom: "env(safe-area-inset-bottom, 0px)",
                }}
            >
                {loading ? (
                    <MuiMaterials.Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: getThemeColor("rgba(232,232,232,0.7)", "rgba(26,26,26,0.7)"),
                        }}
                    >
                        <Spinner size="modal" />
                    </MuiMaterials.Box>
                ) : null}

                <ChromeHeaderBar
                    paper={paper}
                    border={border}
                    textSecondary={textSecondary}
                    onClose={() => navigate("/sites", { replace: true })}
                    leading={
                        phase === "view" ? (
                            <SiteWindowHeaderLeading
                                toolTitle="Cable Health"
                                siteWindowId={SITE_WINDOW_IDS.cableHealth}
                                siteGuid={loadedSiteGuid || pickerSiteGuid || null}
                                textSecondary={textSecondary}
                                border={border}
                                showSiteSelect
                                siteSelectValue={loadedSiteGuid}
                                onSiteChange={handleHeaderSiteChange}
                                sites={sites}
                                filterSite={(s) => !s.isHms}
                                siteSelectDisabled={loading || sitesLoading}
                                includeTool={false}
                            />
                        ) : null
                    }
                    tool={
                        <SiteWindowToolNav
                            toolTitle="Cable Health"
                            siteWindowId={SITE_WINDOW_IDS.cableHealth}
                            siteGuid={loadedSiteGuid || pickerSiteGuid || null}
                            textSecondary={textSecondary}
                        />
                    }
                />

                {phase === "view" && payload?.ok ? (
                    <ChromeActionBar
                        className="hide-scrollbar"
                        paper={paper}
                        border={border}
                        grid={false}
                        sx={HIDE_SCROLLBAR_SX}
                    >
                        {[
                            { label: "Bins", value: stats.bins },
                            { label: "Cables", value: stats.cables },
                            { label: "Sensors", value: stats.sensors },
                            {
                                label: "Cables to replace",
                                value: stats.replace,
                                danger: true,
                                tip:
                                    stats.replaceNames.length > 0
                                        ? stats.replaceNames.join("\n")
                                        : "None",
                            },
                        ].map((item) => {
                            const card = (
                                <MuiMaterials.Box
                                    sx={{
                                        border: `1px solid ${border}`,
                                        borderRadius: 1,
                                        px: 1,
                                        py: 0.35,
                                        minWidth: 64,
                                        background: getThemeColor("#f8f8f8", "#252525"),
                                        cursor: item.tip ? "default" : undefined,
                                    }}
                                >
                                    <MuiMaterials.Typography
                                        sx={{
                                            fontSize: 15,
                                            fontWeight: 700,
                                            lineHeight: 1.15,
                                            color: item.danger
                                                ? getThemeColor("#d32f2f", "#ef5350")
                                                : textPrimary,
                                        }}
                                    >
                                        {item.value}
                                    </MuiMaterials.Typography>
                                    <MuiMaterials.Typography
                                        sx={{ fontSize: 11, color: textSecondary, whiteSpace: "nowrap", lineHeight: 1.2 }}
                                    >
                                        {item.label}
                                    </MuiMaterials.Typography>
                                </MuiMaterials.Box>
                            );
                            if (!item.tip) {
                                return (
                                    <React.Fragment key={item.label}>{card}</React.Fragment>
                                );
                            }
                            return (
                                <MuiMaterials.Tooltip
                                    key={item.label}
                                    arrow
                                    placement="bottom"
                                    title={<span style={{ whiteSpace: "pre-line" }}>{item.tip}</span>}
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                maxWidth: 420,
                                                backgroundColor: getThemeColor("#333333", "#2d2d2d"),
                                                color: getThemeColor("#ffffff", "#f4cc8e"),
                                                fontSize: 12,
                                                lineHeight: 1.4,
                                            },
                                        },
                                        arrow: {
                                            sx: {
                                                color: getThemeColor("#333333", "#2d2d2d"),
                                            },
                                        },
                                    }}
                                >
                                    {card}
                                </MuiMaterials.Tooltip>
                            );
                        })}
                    </ChromeActionBar>
                ) : null}

                {error && phase === "select" ? (
                    <MuiMaterials.Box
                        sx={{
                            mx: 2,
                            mt: 1,
                            flexShrink: 0,
                            p: 1.5,
                            borderRadius: 1,
                            border: `1px solid ${border}`,
                            backgroundColor: getThemeColor("#fff8e1", "#3a3228"),
                        }}
                    >
                        <MuiMaterials.Typography variant="body2" sx={{ m: 0 }}>
                            {error}
                        </MuiMaterials.Typography>
                    </MuiMaterials.Box>
                ) : null}

                {phase === "select" ? (
                    <SiteSelectPhase
                        value={pickerSiteGuid}
                        onChange={setPickerSiteGuid}
                        onContinue={(guid) => {
                            setPickerSiteGuid(guid);
                            loadHealth(guid);
                        }}
                        onCancel={() => navigate("/sites", { replace: true })}
                        loading={sitesLoading || loading}
                        filterSite={(s) => !s.isHms}
                    />
                ) : null}

                {phase === "view" ? (
                    <ModalBody className="hide-scrollbar">
                        {error ? (
                            <MuiMaterials.Typography
                                sx={{ color: getThemeColor("#b71c1c", "#ef9a9a"), mb: 1, flexShrink: 0 }}
                            >
                                {error}
                            </MuiMaterials.Typography>
                        ) : null}
                        <ModalContentTile
                            className="hide-scrollbar"
                            sx={{ overflowY: "auto", p: "12px 16px" }}
                        >
                        {payload?.ok ? (
                            <MuiMaterials.Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    flexWrap: "wrap",
                                    alignItems: "flex-end",
                                    gap: 2.25,
                                    rowGap: 3,
                                }}
                            >
                                    {bins.map((g) => (
                                        <MuiMaterials.Box
                                            key={g.bin}
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                gap: 0.75,
                                                flex: "0 0 auto",
                                            }}
                                        >
                                            <MuiMaterials.Typography sx={{ fontWeight: 600 }}>
                                                Bin {g.bin}
                                            </MuiMaterials.Typography>
                                            <MuiMaterials.Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    alignItems: "stretch",
                                                    justifyContent: "center",
                                                    gap: "1px",
                                                    height: 180,
                                                }}
                                            >
                                                {g.cables.map((c) => {
                                                    const sensors = [...c.sensors].sort(
                                                        (a, b) => a.position - b.position,
                                                    );
                                                    return (
                                                        <MuiMaterials.Box
                                                            key={c.cable}
                                                            sx={{
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                alignItems: "center",
                                                                gap: "2px",
                                                                width: 18,
                                                                height: "100%",
                                                            }}
                                                        >
                                                            <MuiMaterials.Box
                                                                sx={{
                                                                    display: "flex",
                                                                    flexDirection: "column-reverse",
                                                                    gap: "1px",
                                                                    flex: 1,
                                                                    width: "100%",
                                                                }}
                                                            >
                                                                {sensors.map((s) => {
                                                                    const h = sensorHealth(s);
                                                                    const status = s.forcedOpen
                                                                        ? "ForcedOpen"
                                                                        : s.flag;
                                                                    const title = [
                                                                        `Bin: ${c.bin}`,
                                                                        `Cable: ${c.cable}`,
                                                                        `Sensor: ${s.name}`,
                                                                        `Position: ${s.position}`,
                                                                        `Status: ${status}`,
                                                                        `Last read: ${formatLastRead(s.lastHistoryRead)}`,
                                                                        `Invalid day %: ${s.invalidDayPct.toFixed(2)}%`,
                                                                        `Valid day %: ${s.validDayPct.toFixed(2)}%`,
                                                                    ].join("\n");
                                                                    return (
                                                                        <MuiMaterials.Box
                                                                            key={s.name}
                                                                            title={title}
                                                                            sx={{
                                                                                flex: 1,
                                                                                minHeight: 4,
                                                                                width: "100%",
                                                                                borderRadius: 0,
                                                                                background: healthColor(h),
                                                                                boxSizing: "border-box",
                                                                            }}
                                                                        />
                                                                    );
                                                                })}
                                                            </MuiMaterials.Box>
                                                            <MuiMaterials.Typography
                                                                sx={{
                                                                    fontSize: 11,
                                                                    color: textSecondary,
                                                                }}
                                                            >
                                                                {c.cable.includes("-")
                                                                    ? c.cable.split("-").pop()
                                                                    : c.cable}
                                                            </MuiMaterials.Typography>
                                                        </MuiMaterials.Box>
                                                    );
                                                })}
                                            </MuiMaterials.Box>
                                        </MuiMaterials.Box>
                                    ))}
                                </MuiMaterials.Box>
                        ) : null}
                        </ModalContentTile>
                    </ModalBody>
                ) : null}
            </div>
        </MuiMaterials.ThemeProvider>
    );
};

export default CableHealthWindow;
